import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SchemaOrg from '@/components/base/SchemaOrg';
import { articles } from '@/mocks/articles';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return <NotFound />;
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.contentTitle,
    description: article.excerpt,
    url: `https://vigorbuy.casa/article/${article.slug}`,
    image: article.image,
    author: {
      '@type': 'Organization',
      name: 'Vigorbuy Editorial Team',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Vigorbuy Spreadsheet',
      url: 'https://vigorbuy.casa/',
    },
    datePublished: '2026-06-15',
    dateModified: '2026-06-28',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://vigorbuy.casa/article/${article.slug}`,
    },
  };

  return (
    <>
      <SchemaOrg data={articleSchema} />
      <header>
        <Navbar />
      </header>

      <main>
        <article className="pt-24 pb-14 md:pb-20">
          <div className="px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <nav className="mb-8" aria-label="Breadcrumb">
                <ol className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
                  <li>
                    <Link to="/" className="hover:text-primary-600 transition-colors">
                      Home
                    </Link>
                  </li>
                  <li>
                    <i className="ri-arrow-right-s-line text-xs"></i>
                  </li>
                  <li>
                    <span className="text-foreground-700 font-medium">
                      {article.tag}
                    </span>
                  </li>
                </ol>
              </nav>

              <header>
                <span className="inline-block px-4 py-1.5 bg-primary-100 text-primary-700 rounded-full text-xs font-semibold uppercase tracking-wider mb-4">
                  {article.tag}
                </span>
                <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-900 leading-tight">
                  {article.contentTitle}
                </h1>
                <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line"></i>
                    Updated June 2026
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-time-line"></i>
                    {article.readTime}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-line"></i>
                    Vigorbuy Editorial Team
                  </span>
                </div>
              </header>

              <div className="mt-10">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-auto object-cover rounded-lg"
                  loading="eager"
                />
              </div>

              <div className="mt-10 text-foreground-700 leading-relaxed space-y-6">
                {article.content.map((block, idx) => {
                  if (block.type === 'p') {
                    return (
                      <p key={idx} className="text-foreground-600 leading-relaxed">
                        {block.text}
                      </p>
                    );
                  }
                  if (block.type === 'h2') {
                    return (
                      <h2 key={idx} className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-12 mb-4">
                        {block.text}
                      </h2>
                    );
                  }
                  return null;
                })}

                <div className="mt-12 p-6 md:p-8 bg-primary-50 rounded-lg border border-primary-200">
                  <h3 className="font-heading text-xl font-bold text-foreground-900 mb-3">
                    Ready to Start Browsing?
                  </h3>
                  <p className="text-foreground-600 text-sm leading-relaxed mb-5">
                    Access the full Vigorbuy Spreadsheet now and explore thousands of verified products across 14 categories. Find trusted sellers, compare listings, and shop with confidence.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <a
                      href="https://kaspreadsheet.com"
                      target="_blank"
                      rel="noopener nofollow"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap justify-center cursor-pointer"
                    >
                      Open Vigorbuy Sheet
                      <i className="ri-arrow-right-up-line"></i>
                    </a>
                    <Link
                      to="/"
                      className="inline-flex items-center gap-2 px-6 py-3 border border-foreground-300 text-foreground-700 rounded-full text-sm font-semibold hover:bg-foreground-50 transition-all duration-200 whitespace-nowrap justify-center"
                    >
                      <i className="ri-home-line"></i>
                      Back to Homepage
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}